import { Component, Input, OnInit, signal } from '@angular/core';
import { ThermalOperationsService } from '../../services/thermal-operations.service';
import { Observable, catchError, forkJoin, lastValueFrom, map, of } from 'rxjs';
import { OperationEnvelopeUi, PressureResults, TemperatureResults } from '../../models/thermal-results.model';
import { BaseOperation } from '../../models/thermal-operation.model';
import { FluidsService } from '../../services/fluids.service';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { SelectItem } from 'primeng/api';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { Store } from '@ngneat/elf';
import { StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { withUIEntities } from '@ngneat/elf-entities';
import { XyLinePlotUi } from 'src/app/core/models/xy-line-plot.model';
import {
  buildMultipleOperationTraces,
  buildSingleModeTraces,
  convertDefaultEnvelopeByUnits,
  mapTemperatureResultsToOperations,
  uniqueById,
} from './shared/helpers/operation-envelope.helper';
import { OperationEnvelopeOrchestrationService, PhaseEnvelopeCandidate } from './shared/services/operation-envelope-orchestration.service';
import { Fluid } from '../../models/fluid.model';

@Component({
  selector: 'app-operation-envelope',
  templateUrl: './operation-envelope.component.html',
  standalone: false
})
export class OperationEnvelopeComponent implements OnInit {

  constructor(
    private _thermalOperationsService: ThermalOperationsService,
    private _fluidService: FluidsService,
    private _signalRService: SignalRService,
    private _storeService: StoreService,
    private _orchestrationService: OperationEnvelopeOrchestrationService
  ) { }

  private _depthUnit!: string;
  private _temperatureUnit!: string;
  private _pressureUnit!: string;
  private _pressureResults!: PressureResults[];
  private _temperatureResults!: TemperatureResults[];
  private _fluids: Fluid[] = [];
  private _defaultEnvelope = {
    x: [-70, -65, -60, -55, -50, -45, -40, -35, -30, -25, -20, -15, -10, -5, 0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 87.8],
    y: [74.851189, 84.29903685, 94.62512064, 105.8790509, 118.1110952, 131.3721561, 145.7137646, 161.1880918, 177.8479785, 195.7469833, 214.9394527, 235.480611, 257.4266742, 280.8349879, 305.7641921, 332.2744152, 360.4275016, 390.2872765, 421.9198558, 455.3940105, 490.7815977, 528.1580776, 567.6031453, 609.2015188, 653.0439521, 699.2285857, 747.8628336, 799.0661885, 852.9747501, 909.7494477, 969.5939808, 1032.81054, 1069.930683]
  };

  public isLoading = signal<boolean>(true);
  public operations = signal<Partial<BaseOperation>[]>([]);
  public phaseEnvelopes = signal<PhaseEnvelopeCandidate[]>([]);
  public needToCalculate = signal<boolean>(false);
  public calculationError = "Please calculate PERICAL to see results.";
  public resultsGrid: any[] = [];
  public resultsGridPhaseEnvelope: any[] = [];
  public resultsGridOperationData: any[] = [];
  public cols: TableColumnDefinition[] = [];
  public tableName = 'Operation Envelope';
  public moveUpDisabled = signal<boolean>(true);
  public moveDownDisabled = signal<boolean>(false);

  public singleMultiple: SelectItem[] = [
    { label: 'Single', value: 'singleOperation' },
    { label: 'Multiple', value: 'multipleOperation' }
  ];

  public resultsDisplay: SelectItem[] = [
    { label: 'Plot', value: 'plot' },
    { label: 'Grid', value: 'grid' }
  ];

  public envelopeOperation: SelectItem[] = [
    { label: 'Phase Envelope', value: 'phaseEnvelope' },
    { label: 'Operation', value: 'operation' }
  ];

  public plot = {
    data: [] as any[],
    layout: {} as Record<string, unknown>,
    config: {} as Record<string, unknown>
  };

  public yAxisTitle = '';
  public xAxisTitle = '';
  public plotName!: string;
  public downloadPlotName = 'operation_envelope_plot';

  //State Management
  private _componentId!: string;
  @Input() set componentId(value: string) {
    this._componentId = value;
    this.operationEnvelopeStore = this._storeService.createStore(this.componentId, new OperationEnvelopeUi, withUIEntities<XyLinePlotUi>());
    this.selectedSingleMultiple.set(this.operationEnvelopeStore.state.selectedSingleMultiple || 'singleOperation');
    this.selectedResultsDisplay.set(this.operationEnvelopeStore.state.selectedResultsDisplay || 'plot');
    this.selectedEnvelopeOperation.set(this.operationEnvelopeStore.state.selectedEnvelopeOperation || 'phaseEnvelope');
    this.selectedOperation.set(this.operationEnvelopeStore.state.selectedOperation);
    this.selectedPhaseEnvelope.set(this.operationEnvelopeStore.state.selectedPhaseEnvelope);
  }
  get componentId(): string {
    return this._componentId;
  }
  public operationEnvelopeStore!: Store;

  // Local properties for store state (Stage 1 migration)
  public selectedSingleMultiple = signal<string>('singleOperation');
  public selectedResultsDisplay = signal<string>('plot');
  public selectedEnvelopeOperation = signal<string>('phaseEnvelope');
  public selectedOperation = signal<Partial<BaseOperation> | undefined>(undefined);
  public selectedPhaseEnvelope = signal<PhaseEnvelopeCandidate | null>(null);

  async ngOnInit(): Promise<void> {

    const uu = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);
    if (!uu) {
      this.calculationError = 'Units are unavailable. Please reload and try again.';
      this.needToCalculate.set(true);
      this.isLoading.set(false);
      return;
    }

    this.yAxisTitle = `Pressure (${uu.pressure})`
    this.xAxisTitle = `Temperature (${uu.temperature})`
    this._depthUnit = uu.longLengths;
    this._temperatureUnit = uu.temperature;
    this._pressureUnit = uu.pressure;

    this.cols = [
      { field: 'pressure', header: `Pressure (${uu.pressure})`, valueFormatter: (params: any) => this.formatDecimal(params) },
      { field: 'temperature', header: `Temperature (${uu.temperature})`, valueFormatter: (params: any) => this.formatDecimal(params) }
    ];

    this._defaultEnvelope = convertDefaultEnvelopeByUnits(this._defaultEnvelope, uu.temperature, uu.pressure);

    this.getData();

    if (!this.plotName) {
      this.plotName = this.setPlotName();
    }

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));
  }

  private signalRfunc(data: any) {
    if (data.action === PeriforOnChangeMessages.REFRESH_THERMAL_RESULTS || data.action === PeriforOnChangeMessages.REFRESH_STRING_INPUTS) {
      this.plot.data = [];
      this.getData();
    }
  }

  private setPlotName(phaseEnvelopeName?: string): string {
    if (this.selectedSingleMultiple() === 'singleOperation') {
      return 'operationEnvelopePlotSingle - ' + this.selectedOperation()?.id;
    } else {
      return 'operationEnvelopePlotMultiple - ' + phaseEnvelopeName;
    }

  }

  private getData(): void {
    this.isLoading.set(true);
    this.plot.data = [];
    this.operations.set([]);
    this.moveUpDisabled.set(true);
    this.moveDownDisabled.set(false);
    const sources: Observable<any>[] = [
      this._thermalOperationsService.getThermalTemperatureResults(true) as Observable<any>,
      this._thermalOperationsService.getThermalPressureResults(true) as Observable<any>,
      this._thermalOperationsService.getThermalOperations() as Observable<any>,
    ];

    forkJoin(sources).pipe(
      map(([temperatureResults, pressureResults, operations]) => {
        if (temperatureResults.length === 0) {
          this.calculationError = "Please calculate PERICAL to see results.";
          this.needToCalculate.set(true);
          this.isLoading.set(false);
          return;
        }

        const sortByOperationOrder = (a: TemperatureResults, b: TemperatureResults) => {
          return operations.findIndex((x: any) => x.name === a.name) - operations.findIndex((x: any) => x.name === b.name);
        };

        temperatureResults.sort(sortByOperationOrder);
        pressureResults.sort(sortByOperationOrder);

        this.operations.set(mapTemperatureResultsToOperations(temperatureResults));

        this._pressureResults = pressureResults;
        this._temperatureResults = temperatureResults;

        if (this.selectedSingleMultiple() === 'singleOperation' || !this.selectedSingleMultiple()) {
          const selectedOperation = this.selectedOperation();
          if (!selectedOperation?.id) {
            this.getPhaseEnvelope(temperatureResults[0].operationId, 0);
          } else {
            this.getPhaseEnvelope(selectedOperation.id, this._temperatureResults.findIndex(x => x.name === selectedOperation.name));
          }
        } else {
          this.getMultipleData();
        }

      }),
      catchError(() => {
        this.isLoading.set(false);
        return of(null);
      })).subscribe();
  }

  private async getMultipleData(): Promise<void> {
    this.isLoading.set(true);

    if (!this._temperatureResults || this._temperatureResults.length === 0) {
      this.needToCalculate.set(true);
      this.calculationError = "Please calculate PERICAL to see results.";
      this.isLoading.set(false);
      return;
    }

    try {
      const operationsWithCalculatedResults = this._temperatureResults.filter(x => x.phases[0].temperatureResultsTable.rows.length > 0);
      const operations = await Promise.all(
        operationsWithCalculatedResults.map(x => lastValueFrom(this._thermalOperationsService.getThermalOperationById(x['operationId'])))
      ) as any[];

      this.operations.set(operations as any);

      const fluidRequests: Array<Promise<any>> = [];
      operations.forEach((x: any) => {
        if (x.inside?.['fluid']?.id) {
          fluidRequests.push(lastValueFrom(this._fluidService.getFluidById(x.inside['fluid'].id)));
        }

        if (x.inside?.type === 'shutIn') {
          const previousFluidId = this._orchestrationService.getPreviousFluidId(x, operations);
          if (previousFluidId) {
            fluidRequests.push(lastValueFrom(this._fluidService.getFluidById(previousFluidId)));
          }
        }
      });

      const fluids = await Promise.all(fluidRequests);

      this._fluids = uniqueById(
        fluids
          .filter(x => !!x && x.state?.type === 'co2Fluid')
            .filter(x => operations.some((y: any) => y.inside?.['fluid']?.id === x.id))
      );

      if (this._fluids.length === 0) {
        this.calculationError = "Please select GERG2008 as the CO2 EOS option to see results.";
        this.needToCalculate.set(true);
        this.isLoading.set(false);
        this.plot.data = [];
        return;
      }

      const gergFluids = this._fluids.filter((x: any) => x.state?.['co2withImpurities']);
      if (gergFluids.length > 0) {
        const fluidEnvelopes = await Promise.all(gergFluids.map(x => lastValueFrom(this._fluidService.getPhaseEnvelope(x.id))));
        this.phaseEnvelopes.set(fluidEnvelopes.map((envelope, index) => ({
          fluid: gergFluids[index],
          fluidEnvelope: envelope,
        })));
      } else {
        this.phaseEnvelopes.set([]);
      }

      this.getPhaseEnvelopeForMultipleData();
    } catch {
      this.isLoading.set(false);
    }
  }

  private getPhaseEnvelopeForMultipleData(): void {
    const currentPhaseEnvelopes = this.phaseEnvelopes();
    const operations = this.operations();

    const phaseEnvelopes = this._orchestrationService.buildPhaseEnvelopesForMultipleData(
      this._fluids,
      currentPhaseEnvelopes,
      operations,
      this._defaultEnvelope
    );

    this.phaseEnvelopes.set(phaseEnvelopes);
    this.selectedPhaseEnvelope.set(phaseEnvelopes[0]);
    this.operationEnvelopeStore.update(state => ({ ...state, selectedPhaseEnvelope: phaseEnvelopes[0] }));
    this.plotName = this.setPlotName();

    this.doStuffWithMultipleData();
  }

  private async doStuffWithMultipleData(e?: any): Promise<void> {
    this.plot.data = [];
    const pEnvelope = e ?? this.phaseEnvelopes()[0];
    if (!pEnvelope) {
      this.isLoading.set(false);
      return;
    }

    this.plot.data.push({
      x: pEnvelope.fluidEnvelope.x,
      y: pEnvelope.fluidEnvelope.y,
      name: pEnvelope.fluid.state.name,
      color: '#FF0000'
    } as any);

    for (const operation of pEnvelope.operations as any[]) {
      let perforationDepth = operation.inside['perforation']?.measuredDepth;
      if (!perforationDepth) {
        const previousOperation = await lastValueFrom(
          this._thermalOperationsService.getThermalOperationById(operation.previousOperationId)
        );
        const previousInside = (previousOperation as any)?.inside;
        perforationDepth = previousInside?.['perforation']?.measuredDepth;
      }
      if (perforationDepth == null) continue;

      const resultIndex = this._temperatureResults.findIndex(x => x.name === operation.name);
      if (resultIndex < 0) continue;

      const traces = buildMultipleOperationTraces(
        operation, resultIndex, perforationDepth,
        pEnvelope.operations,
        this._temperatureResults, this._pressureResults,
        this._temperatureUnit, this._pressureUnit, this._depthUnit
      );
      this.plot.data.push(...traces);
    }

    this.calculationError = "";
    this.needToCalculate.set(false);
    this.isLoading.set(false);
  }

  public onPhaseEnvelopeSelected(phaseEnvelope: any): void {
    this.isLoading.set(true);
    this.selectedPhaseEnvelope.set(phaseEnvelope);
    this.operationEnvelopeStore.update(state => ({ ...state, selectedPhaseEnvelope: phaseEnvelope }));
    this.plotName = this.setPlotName(phaseEnvelope?.fluid?.state?.name);
    this.plot.data = [];
    this.phaseEnvelopes().forEach(candidate => {
      if (candidate.fluid.id === phaseEnvelope?.fluid?.id) {
        this.doStuffWithMultipleData(candidate);
      }
    });
  }

  public modeToggle(e: string): void {
    // this.moveUpDisabled = true;
    // this.moveDownDisabled = false;
    this.plot.data = [];
    this.selectedSingleMultiple.set(e);
    this.operationEnvelopeStore.update(state => ({ ...state, selectedSingleMultiple: e }));
    if (e === 'singleOperation') {
      this.selectedOperation.set(this.operations()[0]);
      this.operationEnvelopeStore.update(state => ({ ...state, selectedOperation: this.operations()[0] }));
      this.plotName = this.setPlotName();
      this.getData();
    } else {
      this.calculationError = "";
      this.needToCalculate.set(false);
      this.selectedResultsDisplay.set('plot');
      this.selectedPhaseEnvelope.set(this.phaseEnvelopes()[0]);
      this.operationEnvelopeStore.update(state => ({ ...state, selectedResultsDisplay: 'plot', selectedPhaseEnvelope: this.phaseEnvelopes()[0] }));
      this.plotName = this.setPlotName();
      this.getMultipleData();
    }
  }

  public onOperationSelected(operation: any): void {
    this.isLoading.set(true);
    if (this._temperatureResults.length === 0) {
      this.needToCalculate.set(true);
      this.calculationError = "Please calculate PERICAL to see results.";
    }

    this.selectedOperation.set(operation);
    this.operationEnvelopeStore.update(state => ({ ...state, selectedOperation: operation }));
    this.plotName = this.setPlotName();
    const resultIndex = this._temperatureResults.findIndex(x => x.name === operation.name);
    if (resultIndex < 0) {
      this.isLoading.set(false);
      return;
    }
    const operationId = this._temperatureResults[resultIndex].operationId;

    this.moveUpDisabled.set(resultIndex === 0);
    this.moveDownDisabled.set(resultIndex === this._temperatureResults.length - 1);

    this.plot.data = [];
    this.getPhaseEnvelope(operationId, resultIndex);
  }

  private async getPhaseEnvelope(operationId: string, resultIndex: number): Promise<void> {
    try {
      const operationsWithCalculatedResults = this._temperatureResults.filter(x => x.phases[0].temperatureResultsTable.rows.length > 0);
      const sources = operationsWithCalculatedResults.map(x => this._thermalOperationsService.getThermalOperationById(x['operationId']));
      const operations = await lastValueFrom(forkJoin(sources)) as any[];

      this.operations.set(operations);

      const operation = operations.find(x => x.id === operationId);
      if (!operation) {
        this.isLoading.set(false);
        return;
      }

      const co2EosOption = operation.inside?.['flow']?.co2EosOption;
      const isShutIn = operation.inside?.type === 'shutIn';

      if (co2EosOption !== 'GERG2008') {
        if (isShutIn && operation.previousOperationId) {
          const previousOperationId = this._orchestrationService.getPreviousOperationIdWithFluid(operation, operations);
          const previousOperation = operations.find(op => op.id === previousOperationId || op.operationId === previousOperationId);

          if (previousOperation?.inside?.['flow']?.co2EosOption === 'GERG2008') {
            await this.getFluidEnvelope(previousOperation.inside['fluid'].id, resultIndex, operation);
            return;
          }
        }

        this.returnCalculateGerg2008Error(operation);
        return;
      }

      if (this.plot.data.length > 1 || isShutIn || co2EosOption === 'ImportFile') {
        return;
      }

      await this.getFluidEnvelope(operation.inside['fluid'].id, resultIndex, operation);
    } catch {
      this.isLoading.set(false);
    }
  }

  private returnCalculateGerg2008Error(operation: any) {
    this.calculationError = "Please select GERG2008 as the CO2 EOS option to see results.";
    this.needToCalculate.set(true);
    this.isLoading.set(false);
    this.plot.data = [];
    this.selectedOperation.set(this.operations().find(x => x.name === operation.name));
    this.operationEnvelopeStore.update(state => ({ ...state, selectedOperation: this.selectedOperation() }));
    this.plotName = this.setPlotName();
    return;
  }

  private async getFluidEnvelope(fluidId: string, resultIndex: number, operation: any): Promise<void> {
    try {
      const fluid = await lastValueFrom(this._fluidService.getFluidById(fluidId));
      if (fluid?.state?.type !== 'co2Fluid') {
        this.calculationError = "Please select GERG2008 as the CO2 EOS option to see results.";
        this.needToCalculate.set(true);
        this.isLoading.set(false);
        this.plot.data = [];
        return;
      }

      const hasImpurities = fluid?.state?.['co2withImpurities'] === true || fluid?.state?.['co2withImpurities']?.toString() === 'true';
      const envelopeData = hasImpurities
        ? await lastValueFrom(this._fluidService.getPhaseEnvelope(fluidId))
        : this._defaultEnvelope;

      if (this.plot.data.length === 2) {
        return;
      }

      await this.plotData(envelopeData, resultIndex, operation);
      this.calculationError = "";
      this.needToCalculate.set(false);
    } catch {
      this.isLoading.set(false);
    }
  }

  private async plotData(data: any, resultIndex: number, operation: any): Promise<void> {
    let perforationDepth = operation.inside['perforation']?.measuredDepth;
    if (!perforationDepth) {
      const previousOperation = await lastValueFrom(
        this._thermalOperationsService.getThermalOperationById(operation.previousOperationId)
      );
      const previousInside = (previousOperation as any)?.inside;
      perforationDepth = previousInside?.['perforation']?.measuredDepth;
    }

    const { traces, gridPhaseEnvelope, gridOperationData } = buildSingleModeTraces(
      data, resultIndex, operation, perforationDepth,
      this._temperatureResults, this._pressureResults,
      this._temperatureUnit, this._pressureUnit, this._depthUnit,
      this.cols.map(c => c.field)
    );

    this.plot.data = traces;
    this.resultsGridPhaseEnvelope = gridPhaseEnvelope;
    this.resultsGridOperationData = gridOperationData;

    this.resultsGrid = this.selectedEnvelopeOperation() === 'phaseEnvelope' ? this.resultsGridPhaseEnvelope : this.resultsGridOperationData;
    this.operations.set(this._temperatureResults.map(x => ({
      name: x.name,
      operationId: x.operationId,
    })));
    this.selectedOperation.set(this.operations().find(x => x.name === operation.name));
    this.operationEnvelopeStore.update(state => ({ ...state, selectedOperation: this.selectedOperation() }));
    this.isLoading.set(false);
  }

  public selectedTableChange(value: string): void {
    this.selectedEnvelopeOperation.set(value);
    this.operationEnvelopeStore.update(state => ({ ...state, selectedEnvelopeOperation: value }));
    this.plotName = this.setPlotName();

    this.resultsGrid = value === 'phaseEnvelope' ? this.resultsGridPhaseEnvelope : this.resultsGridOperationData;
  }

  public onSelectedResultsDisplayUpdated(value: string): void {
    this.selectedResultsDisplay.set(value);
    this.operationEnvelopeStore.update(state => ({ ...state, selectedResultsDisplay: value }));
    this.plotName = this.setPlotName();
  }

  private formatDecimal(value: number | null | undefined): string | null {
    return value || value === 0
      ? (Math.trunc(value * 100) / 100).toLocaleString('en-US')
      : null;
  }

  onUpDownButtonClick(e: 'up' | 'down'): void {
    this.isLoading.set(true);
    const currentOperationIndex = this._temperatureResults.findIndex(x => x.name === this.selectedOperation()?.name);
    const nextOperationIndex = e === 'up' ? currentOperationIndex - 1 : currentOperationIndex + 1;
    this.moveUpDisabled.set(nextOperationIndex - 1 < 0);
    this.moveDownDisabled.set(nextOperationIndex >= this._temperatureResults.length - 1);

    if (nextOperationIndex < 0 || nextOperationIndex >= this._temperatureResults.length) {
      this.isLoading.set(false);
      return;
    }

    const operationId = this._temperatureResults[nextOperationIndex].operationId;
    this.plot.data = [];
    this.getPhaseEnvelope(operationId, nextOperationIndex);
  }
}

interface TableColumnDefinition {
  field: string;
  header: string;
  valueFormatter: (params: number | null | undefined) => string | null;
}
